import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './TenantDetailPage.scss';
import UIButton from '@/components/shared/UIButton';
import UIPageTitle from '@/components/shared/UIPageTitle';
import TenantOverviewSection from '@/components/dashboard/Tenant/TenantOverviewSection';
import TenantInformationSection from '@/components/dashboard/Tenant/TenantInformationSection';
import UnitInformationSection from '@/components/dashboard/Tenant/UnitInformationSection';
import DocumentsSection from '@/components/shared/Account/DocumentsSection';
import TenantActionsSection from '@/components/dashboard/Tenant/TenantActionsSection';
import useTenant from '@/hooks/useTenant';
import { useDocuments } from '@/hooks/useDocuments';

const TenantDetailPage = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = useTenant(tenantId);
  const {
    documents,
    loading: docLoading,
    error: docError,
    onUpload,
    onUpdate,
    onDelete,
  } = useDocuments(tenantId);

  if (isLoading) return <div>Loading tenant...</div>;
  if (error) {
    console.error('Failed to load tenant data:', error);
    return <div>Error loading tenant data.</div>;
  }
  if (!data || !data.tenant) {
    return <div>Tenant not found.</div>;
  }

  const { tenant, leases, lease } = data;

  const adaptedTenantData = {
    id: tenant.id,
    firstName: tenant.firstname,
    lastName: tenant.lastname,
    email: tenant.email,
    phone: tenant.phone || '',
    avatar: tenant.avatar,
    lease: lease?.status || 'N/A',
    moveIn: lease?.startDate || 'N/A',
    moveOut: lease?.endDate || 'N/A',
    rent: lease ? `$${lease.monthlyRent}/month` : 'N/A',
    deposit: '$0', // TODO: Add deposit to API
    status:
      lease?.status === 'active' ? ('paid' as const) : ('pending' as const),
    personal_country: tenant.personal?.personal_country,
    personal_province: tenant.personal?.personal_province,
    personal_city: tenant.personal?.personal_city,
    personal_address: tenant.personal?.personal_address,
    personal_apt_suite: tenant.personal?.personal_apt_suite,
    personal_unit: tenant.personal?.personal_unit,
    personal_municipality: tenant.personal?.personal_municipality,
    personal_postal_code: tenant.personal?.personal_postal_code,
    documents,
  };

  const headerActions = (
    <div className="TenantDetailPage__header-actions">
      <UIButton
        outline={true}
        variant="primary"
        size="medium"
        onClick={() => navigate('/dashboard/tenants')}
      >
        Back
      </UIButton>
    </div>
  );

  return (
    <>
      <UIPageTitle
        title={`Tenant: ${tenant.firstname} ${tenant.lastname}`}
        action={headerActions}
      />

      <div className="TenantDetailPage">
        <TenantOverviewSection
          data={adaptedTenantData}
          onContactTenant={() =>
            navigate(`/dashboard/chats?chatId=${tenantId}`)
          }
          onEditTenant={() => navigate(`/dashboard/tenants/${tenantId}/edit`)}
        />

        <TenantInformationSection
          tenant={tenant}
          onSave={(data) => console.log('Save tenant info:', data)}
          onCancel={() => console.log('Cancel edit')}
        />

        <UnitInformationSection
          leases={leases || []}
          onViewUnitDetails={(leaseId, unitId, propertyId) =>
            navigate(`/dashboard/properties/${propertyId}/units/${unitId}`)
          }
          onUnlinkTenant={(leaseId) =>
            console.log('Unlink tenant for lease ' + leaseId)
          }
          onContactTenant={(tenantId) =>
            navigate(`/dashboard/chats?chatId=${tenantId}`)
          }
        />

        <DocumentsSection
          documents={documents}
          onUpload={onUpload}
          onUpdate={onUpdate}
          onDelete={onDelete}
          loading={docLoading}
          error={docError}
        />

        <TenantActionsSection
          onRemoveTenant={() => console.log('Remove tenant')}
        />
      </div>
    </>
  );
};

export default TenantDetailPage;
